import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import WizardShell from '../ui/wizard-shell';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { relationshipOptionsFor } from '../../lib/subprofileRelationships';
interface Person {
  id: string;
  name: string;
  [key: string]: any;
}
interface SubProfileFormData {
  salutation?: 'Herr' | 'Frau' | 'Divers' | 'Firma';
  relationshipType?: string;
  firstName: string;
  lastName: string;
  name?: string; // optional; wird aus first+last gebildet, falls leer
  birthDate: string;
  communications: { type: string; value: string }[];
}
interface SubProfileFormProps {
  parent: Person | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: SubProfileFormData) => void;
  initialData?: Partial<SubProfileFormData> | null;
}
const SubProfileForm = ({
  parent,
  isOpen,
  onClose,
  onSave,
  initialData
}: SubProfileFormProps) => {
  const [formData, setFormData] = useState<SubProfileFormData>({
    salutation: undefined,
    relationshipType: '',
    firstName: '',
    lastName: '',
    name: '',
    birthDate: '',
    communications: [],
  });
  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        salutation: (initialData.salutation as any) ?? undefined,
        relationshipType: initialData.relationshipType ?? '',
        firstName: initialData.firstName ?? '',
        lastName: initialData.lastName ?? '',
        name: initialData.name ?? '',
        birthDate: initialData.birthDate ?? '',
        communications: initialData.communications ? [...initialData.communications] : [],
      }));
    } else {
      setFormData({
        salutation: undefined,
        relationshipType: '',
        firstName: '',
        lastName: '',
        name: '',
        birthDate: '',
        communications: [],
      });
    }
  }, [initialData]);
  if (!parent) return null;
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      // Passt die Beziehung nicht mehr zur Anrede, wird sie geleert (T-0056).
      if (
        name === 'salutation' &&
        prev.relationshipType &&
        !relationshipOptionsFor(value).some((o) => o.value === prev.relationshipType)
      ) {
        next.relationshipType = '';
      }
      return next;
    });
  };
  const addCommunication = () => {
    setFormData(prev => ({
      ...prev,
      communications: [ ...(prev.communications || []), { type: 'mobil', value: '' } ]
    }));
  };
  const updateCommunication = (index: number, field: 'type' | 'value', value: string) => {
    setFormData(prev => {
      const list = [...(prev.communications || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, communications: list };
    });
  };
  const removeCommunication = (index: number) => {
    setFormData(prev => {
      const list = [...(prev.communications || [])];
      list.splice(index, 1);
      return { ...prev, communications: list };
    });
  };
  const submit = () => {
    const payload: SubProfileFormData = {
      ...formData,
      name: formData.name && formData.name.trim() !== '' ? formData.name : `${formData.firstName} ${formData.lastName}`.trim(),
      communications: formData.communications || [],
    };
    onSave(payload);
  };
  const canPrimary = (formData.firstName || '').trim() !== '' && (formData.lastName || '').trim() !== '';

  return (
    <WizardShell
      open={isOpen}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
      title={initialData ? 'Schülerprofil bearbeiten' : 'Neues Schülerprofil hinzufügen'}
      description={
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs">ID: {parent.id}</span>
            {parent.wpUserId ? <span className="font-mono text-xs">WP: {parent.wpUserId}</span> : null}
          </div>
          <div>Für {parent.name}</div>
        </div>
      }
      contentClassName="w-[1080px] h-[930px] max-w-[95vw] max-h-[95vh]"
      onCancel={onClose}
      cancelLabel="Abbrechen"
      onPrimary={submit}
      primaryLabel={initialData ? 'Speichern' : 'Schülerprofil erstellen'}
      canPrimary={canPrimary}
    >
      <div className="py-4 space-y-4">
        <div>
          <label htmlFor="salutation" className="text-sm font-medium">Anrede</label>
          <Select value={formData.salutation || ''} onValueChange={value => handleSelectChange('salutation', value)}>
            <SelectTrigger id="salutation" className="mt-1">
              <SelectValue placeholder="Anrede auswählen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Herr">Herr</SelectItem>
              <SelectItem value="Frau">Frau</SelectItem>
              <SelectItem value="Divers">Divers</SelectItem>
              <SelectItem value="Firma">Firma</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label htmlFor="relationshipType" className="text-sm font-medium">Beziehung zur Hauptperson</label>
          <Select value={formData.relationshipType || ''} onValueChange={value => handleSelectChange('relationshipType', value)}>
            <SelectTrigger id="relationshipType" className="mt-1">
              <SelectValue placeholder="Beziehung auswählen" />
            </SelectTrigger>
            <SelectContent>
              {relationshipOptionsFor(formData.salutation).map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="firstName" className="text-sm font-medium">Vorname</label>
            <Input id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} className="mt-1" required />
          </div>
          <div>
            <label htmlFor="lastName" className="text-sm font-medium">Nachname</label>
            <Input id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} className="mt-1" required />
          </div>
        </div>

        <div>
          <label htmlFor="name" className="text-sm font-medium">Anzeigename (optional)</label>
          <Input id="name" name="name" value={formData.name} onChange={handleInputChange} placeholder="Falls leer: wird aus Vor- und Nachname gebildet" className="mt-1" />
        </div>

        <div>
          <label htmlFor="birthDate" className="text-sm font-medium">Geburtsdatum</label>
          <Input id="birthDate" name="birthDate" type="date" value={formData.birthDate} onChange={handleInputChange} className="mt-1" />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Kommunikation</label>
          <div className="space-y-2">
            {(formData.communications || []).map((c, idx) => (
              <div key={idx} className="grid gap-2 md:grid-cols-6 items-center">
                <div className="md:col-span-2">
                  <Select value={c.type} onValueChange={val => updateCommunication(idx, 'type', val)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Typ" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mobil">Mobil</SelectItem>
                      <SelectItem value="telefon">Telefon</SelectItem>
                      <SelectItem value="email_alt">Alternative E-Mail</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="telegram">Telegram</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-3">
                  <Input placeholder="Wert" value={c.value} onChange={e => updateCommunication(idx, 'value', e.target.value)} />
                </div>
                <div className="md:col-span-1 flex justify-end">
                  <Button type="button" variant="outline" className="text-destructive" onClick={() => removeCommunication(idx)}>
                    Entfernen
                  </Button>
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" onClick={addCommunication}>Hinzufügen</Button>
          </div>
        </div>
      </div>
    </WizardShell>
  );
};
export default SubProfileForm;